import { useState, useEffect } from 'react';
import KPICard, { fmt, fmtPct, calcDelta } from '../components/KPI';
import { WeeklySalesChart, MarketShareChart } from '../components/Charts';
import { fetchData } from '../hooks/useDataRefresh';

// Derive the latest week-ending date from scanv2-weekly.json at render time
// so the page auto-advances on each refresh. Was hardcoded to '17 Mar 2026'.
function latestWeekEnding(weekly) {
  if (!weekly?.length) return '';
  const maxDate = weekly.reduce((a, r) => (r.date_ty && (!a || r.date_ty > a) ? r.date_ty : a), null);
  if (!maxDate) return '';
  const d = new Date(maxDate);
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

// Average consumer selling price for RSV → Units conversion
const AVG_PRICE_WW = 7.50;
const AVG_PRICE_COLES = 7.80;

// Brand → Assortment → Sub-category hierarchy
const BRAND_ASSORTMENTS = {
  'Bhuja': {
    'Bhuja 350g': 'Snacks - Nuts',
    'Bhuja 140-160g': 'Snacks - Nuts',
    'Bhuja Desi 70-150g': 'Snacks - Nuts',
  },
  'Infuzions': {
    'Infuzions 90g Sharepacks': 'Chips - Sharing',
    'Infuzions Air Fries 90g': 'Chips - Sharing',
  },
  'Infuzions Multipack': {
    'Infuzions 50-75g Multipacks': 'Chips - Multipacks',
    'Infuzions Air Fries 75g Multipacks': 'Chips - Multipacks',
  },
};

const SUBCATEGORIES = ['All', 'Snacks - Nuts', 'Chips - Sharing', 'Chips - Multipacks'];

// Brand proportions from YTD SCAN data (Bhuja 44%, Infuzions 25%, Infuzions MP 31% of WW)
const WW_BRAND_PROPS = { 'Bhuja': 0.437, 'Infuzions': 0.253, 'Infuzions Multipack': 0.310 };
const COLES_BRAND_PROPS = { 'Bhuja': 0.554, 'Infuzions': 0.167, 'Infuzions Multipack': 0.279 };

// Assortment proportions as % of total weekly sales (brand prop × assortment share within brand)
const WW_ASSORTMENT_PROPS = {
  'Bhuja 350g':                    0.437 * 0.50,
  'Bhuja 140-160g':                0.437 * 0.35,
  'Bhuja Desi 70-150g':            0.437 * 0.15,
  'Infuzions 90g Sharepacks':      0.253 * 0.55,
  'Infuzions Air Fries 90g':       0.253 * 0.45,
  'Infuzions 50-75g Multipacks':   0.310 * 0.60,
  'Infuzions Air Fries 75g Multipacks': 0.310 * 0.40,
};
const COLES_ASSORTMENT_PROPS = {
  'Bhuja 350g':                    0.554 * 0.48,
  'Bhuja 140-160g':                0.554 * 0.36,
  'Bhuja Desi 70-150g':            0.554 * 0.16,
  'Infuzions 90g Sharepacks':      0.167 * 0.60,
  'Infuzions Air Fries 90g':       0.167 * 0.40,
  'Infuzions 50-75g Multipacks':   0.279 * 0.55,
  'Infuzions Air Fries 75g Multipacks': 0.279 * 0.45,
};

// Sub-category → brand mapping
const SUBCAT_BRAND = {
  'Snacks - Nuts': 'Bhuja',
  'Chips - Sharing': 'Infuzions',
  'Chips - Multipacks': 'Infuzions Multipack',
};

// Map the UI's brand/assortment labels to scanv2-sku-weekly.json's brand +
// assortment values. Returns a predicate over a SKU record. Null = unmapped,
// which means fall back to proportional synthesis.
function skuMatcher({ brand, assortment, subcat }) {
  const brandUpper = b => (b || '').toUpperCase();
  const isMultipack = a => brandUpper(a).includes('MULTIPACK');

  if (assortment !== 'All') {
    const assortmentMap = {
      'Bhuja 350g':                        ['BHUJA 300-400G'],
      'Bhuja 140-160g':                    ['BHUJA 70-200G', 'BHUJA DELI STYLE NUTS 140G'],
      'Bhuja Desi 70-150g':                ['BHUJA AUTHENTIC INDIAN 70-150G'],
      'Infuzions 90g Sharepacks':          ['INFUZIONS 90-110G'],
      'Infuzions Air Fries 90g':           ['INFUZIONS AIR FRIES SHARE PACK 90G'],
      'Infuzions 50-75g Multipacks':       ['INFUZIONS MULTIPACK 50-75G'],
      'Infuzions Air Fries 75g Multipacks': ['INFUZIONS AIR FRIES MULTIPACK 75G'],
    };
    const targets = assortmentMap[assortment];
    if (!targets) return null;
    return s => targets.includes(brandUpper(s.assortment));
  }

  if (brand !== 'All') {
    if (brand === 'Bhuja')               return s => brandUpper(s.brand) === 'BHUJA';
    if (brand === 'Infuzions')           return s => brandUpper(s.brand) === 'INFUZIONS' && !isMultipack(s.assortment);
    if (brand === 'Infuzions Multipack') return s => brandUpper(s.brand) === 'INFUZIONS' && isMultipack(s.assortment);
    return null;
  }

  if (subcat !== 'All') {
    const mapped = SUBCAT_BRAND[subcat];
    return skuMatcher({ brand: mapped, assortment: 'All', subcat: 'All' });
  }
  return null;
}

// Aggregate per-week TY/LY RSV + units from sku-weekly SKUs matching a
// predicate. Returns {ww: Map<week, {ty_rsv, ly_rsv, ty_units, ly_units}>,
// coles: same}. Week numbers are FY26 1..52.
function aggregateSkuWeekly(skus, matcher) {
  const emptyAgg = () => ({ ty_rsv: 0, ly_rsv: 0, ty_units: 0, ly_units: 0 });
  const ww = new Map();
  const coles = new Map();
  for (const sku of skus) {
    if (!matcher(sku)) continue;
    for (const w of sku.ww || []) {
      const bucket = ww.get(w.week) || emptyAgg();
      bucket.ty_rsv   += w.ty_rsv   || 0;
      bucket.ly_rsv   += w.ly_rsv   || 0;
      bucket.ty_units += w.ty_units || 0;
      bucket.ly_units += w.ly_units || 0;
      ww.set(w.week, bucket);
    }
    for (const w of sku.coles || []) {
      const bucket = coles.get(w.week) || emptyAgg();
      bucket.ty_rsv   += w.ty_rsv   || 0;
      bucket.ly_rsv   += w.ly_rsv   || 0;
      bucket.ty_units += w.ty_units || 0;
      bucket.ly_units += w.ly_units || 0;
      coles.set(w.week, bucket);
    }
  }
  return { ww, coles };
}

// FY26 W1 starts 2025-07-02. Derive date_ty → fiscal week mapping once per
// input weekly array so we can join to sku-weekly rows.
function fyWeekForDate(dateStr) {
  const w1Start = Date.UTC(2025, 6, 2); // months are 0-indexed: 6 = July
  const d = new Date(dateStr);
  const diffDays = Math.round((Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) - w1Start) / 86400000);
  return Math.floor(diffDays / 7) + 1;
}

function applyBrandFilter(weekly, skus, { brand, assortment, subcat }) {
  if (brand === 'All' && assortment === 'All' && subcat === 'All') return { rows: weekly, isEstimate: false };

  const matcher = skuMatcher({ brand, assortment, subcat });

  // If we have a mapping AND real sku-weekly data, replace the filtered rows
  // with actual summed TY/LY per SKU. Otherwise fall back to the old
  // proportional method.
  if (matcher && skus?.length) {
    const { ww, coles } = aggregateSkuWeekly(skus, matcher);
    const rows = weekly.map(w => {
      const fw = fyWeekForDate(w.date_ty);
      const wwAgg = ww.get(fw);
      const colesAgg = coles.get(fw);
      return {
        ...w,
        ww_ty:          wwAgg    ? Math.round(wwAgg.ty_rsv)     : null,
        ww_ly:          wwAgg    ? Math.round(wwAgg.ly_rsv)     : null,
        coles_ty:       colesAgg ? Math.round(colesAgg.ty_rsv)  : null,
        coles_ly:       colesAgg ? Math.round(colesAgg.ly_rsv)  : null,
        ww_units_ty:    wwAgg    ? wwAgg.ty_units               : null,
        ww_units_ly:    wwAgg    ? wwAgg.ly_units               : null,
        coles_units_ty: colesAgg ? colesAgg.ty_units            : null,
        coles_units_ly: colesAgg ? colesAgg.ly_units            : null,
        ww_cat:         w.ww_cat,
        coles_cat:      w.coles_cat,
      };
    });
    return { rows, isEstimate: false };
  }

  // Fallback — proportional synthesis (still labelled est.)
  let prop, colesProp;
  if (assortment !== 'All') {
    prop      = WW_ASSORTMENT_PROPS[assortment]    ?? 1;
    colesProp = COLES_ASSORTMENT_PROPS[assortment] ?? 1;
  } else if (brand !== 'All') {
    prop      = WW_BRAND_PROPS[brand]    ?? 1;
    colesProp = COLES_BRAND_PROPS[brand] ?? 1;
  } else {
    const mappedBrand = SUBCAT_BRAND[subcat];
    prop      = WW_BRAND_PROPS[mappedBrand]    ?? 1;
    colesProp = COLES_BRAND_PROPS[mappedBrand] ?? 1;
  }
  const rows = weekly.map(w => ({
    ...w,
    ww_ty:          w.ww_ty          != null ? w.ww_ty * prop                   : null,
    ww_ly:          w.ww_ly          != null ? w.ww_ly * prop                   : null,
    coles_ty:       w.coles_ty       != null ? w.coles_ty * colesProp           : null,
    coles_ly:       w.coles_ly       != null ? w.coles_ly * colesProp           : null,
    ww_units_ty:    w.ww_units_ty    != null ? Math.round(w.ww_units_ty * prop) : null,
    ww_units_ly:    w.ww_units_ly    != null ? Math.round(w.ww_units_ly * prop) : null,
    coles_units_ty: w.coles_units_ty != null ? Math.round(w.coles_units_ty * colesProp) : null,
    coles_units_ly: w.coles_units_ly != null ? Math.round(w.coles_units_ly * colesProp) : null,
    ww_cat:         w.ww_cat,
    coles_cat:      w.coles_cat,
  }));
  return { rows, isEstimate: true };
}

export default function NAMSales() {
  const [weekly, setWeekly] = useState([]);
  const [ytd, setYTD] = useState(null);
  const [brandData, setBrandData] = useState([]);
  const [skus, setSkus] = useState([]);
  const [retailer, setRetailer] = useState('Both');
  const [period, setPeriod] = useState('13W');
  const [metric, setMetric] = useState('RSV');
  const [brand, setBrand] = useState('All');
  const [assortment, setAssortment] = useState('All');
  const [subcat, setSubcat] = useState('All');

  useEffect(() => {
    Promise.all([
      fetchData('scanv2-weekly.json'),
      fetchData('scanv2-ytd.json'),
      fetchData('scanv2-brand.json'),
      fetchData('scanv2-sku-weekly.json'),
    ]).then(([w, y, b, s]) => {
      setWeekly(w);
      setYTD(y);
      setBrandData(b);
      setSkus(Array.isArray(s) ? s : (s?.skus ?? []));
    });
  }, []);

  const weekEnding = latestWeekEnding(weekly);

  const weeks = { '1W': 1, '13W': 13, '26W': 26, '52W': 52 }[period] || 13;
  const { rows: filteredWeekly, isEstimate } = applyBrandFilter(weekly, skus, { brand, assortment, subcat });
  const recent = filteredWeekly.slice(-weeks);
  const wwLatest = recent.at(-1)?.ww_ty;
  const wwLatestLY = recent.at(-1)?.ww_ly;
  const colesLatest = recent.at(-1)?.coles_ty;
  const colesLatestLY = recent.at(-1)?.coles_ly;

  const showWW = retailer !== 'Coles';
  const showColes = retailer !== 'Woolworths';

  // YTD scaled for active filter (assortment > brand > subcat)
  let brandProp, colesBrandProp;
  if (assortment !== 'All') {
    brandProp = WW_ASSORTMENT_PROPS[assortment] ?? 1;
    colesBrandProp = COLES_ASSORTMENT_PROPS[assortment] ?? 1;
  } else if (brand !== 'All') {
    brandProp = WW_BRAND_PROPS[brand] ?? 1;
    colesBrandProp = COLES_BRAND_PROPS[brand] ?? 1;
  } else if (subcat !== 'All') {
    const mb = SUBCAT_BRAND[subcat];
    brandProp = WW_BRAND_PROPS[mb] ?? 1;
    colesBrandProp = COLES_BRAND_PROPS[mb] ?? 1;
  } else {
    brandProp = 1;
    colesBrandProp = 1;
  }
  const wwYTD_rsv = ytd ? ytd.ww_ytd_ty * brandProp : null;
  const wwYTDLY_rsv = ytd ? ytd.ww_ytd_ly * brandProp : null;
  const colesYTD_rsv = ytd ? ytd.coles_ytd_ty * colesBrandProp : null;
  const colesYTDLY_rsv = ytd ? ytd.coles_ytd_ly * colesBrandProp : null;

  // Units — use actual SCANv2 data, scaled by brand/assortment proportion
  const toU = (rsv, price) => rsv != null ? rsv / price : null;
  const wwYTD = metric === 'Units'
    ? (ytd ? ytd.ww_units_ty * brandProp : null)
    : wwYTD_rsv;
  const wwYTDLY = metric === 'Units'
    ? (ytd ? ytd.ww_units_ly * brandProp : null)
    : wwYTDLY_rsv;
  const colesYTD = metric === 'Units'
    ? (ytd ? ytd.coles_units_ty * colesBrandProp : null)
    : colesYTD_rsv;
  const colesYTDLY = metric === 'Units'
    ? (ytd ? ytd.coles_units_ly * colesBrandProp : null)
    : colesYTDLY_rsv;
  const lastWW = recent.at(-1);
  const lastColes = recent.at(-1);
  const wwLatestDisp = metric === 'Units'
    ? (lastWW?.ww_units_ty ?? toU(wwLatest, AVG_PRICE_WW))
    : wwLatest;
  const wwLatestLYDisp = metric === 'Units'
    ? (lastWW?.ww_units_ly ?? toU(wwLatestLY, AVG_PRICE_WW))
    : wwLatestLY;
  const colesLatestDisp = metric === 'Units'
    ? (lastColes?.coles_units_ty ?? toU(colesLatest, AVG_PRICE_COLES))
    : colesLatest;
  const colesLatestLYDisp = metric === 'Units'
    ? (lastColes?.coles_units_ly ?? toU(colesLatestLY, AVG_PRICE_COLES))
    : colesLatestLY;

  const fmtMetric = v => {
    if (v == null) return '—';
    if (metric === 'Units') {
      if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M u`;
      if (v >= 1e3) return `${(v / 1e3).toFixed(0)}K u`;
      return `${Math.round(v)} u`;
    }
    return fmt(v);
  };

  // Assortment options cascade from selected brand
  const assortmentOptions = brand === 'All'
    ? ['All', ...Object.values(BRAND_ASSORTMENTS).flatMap(a => Object.keys(a))]
    : ['All', ...Object.keys(BRAND_ASSORTMENTS[brand] || {})];

  // Reset assortment when brand changes
  const handleBrandChange = (b) => { setBrand(b); setAssortment('All'); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* Filters Row 1 — Retailer + Period + Metric */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--text-muted)', fontSize: 12, marginRight: 4 }}>Retailer</span>
        {['Both', 'Woolworths', 'Coles'].map(r => (
          <button key={r} className={retailer === r ? 'pill-active' : 'pill-inactive'} onClick={() => setRetailer(r)}>{r}</button>
        ))}
        <div style={{ width: 1, height: 20, background: 'var(--border-soft)', margin: '0 8px' }} />
        <span style={{ color: 'var(--text-muted)', fontSize: 12, marginRight: 4 }}>Period</span>
        {['1W', '13W', '26W', '52W'].map(p => (
          <button key={p} className={period === p ? 'pill-active' : 'pill-inactive'} onClick={() => setPeriod(p)}>{p}</button>
        ))}
        <div style={{ width: 1, height: 20, background: 'var(--border-soft)', margin: '0 8px' }} />
        <span style={{ color: 'var(--text-muted)', fontSize: 12, marginRight: 4 }}>View</span>
        {['RSV', 'Units'].map(m => (
          <button key={m} className={metric === m ? 'pill-active' : 'pill-inactive'} onClick={() => setMetric(m)}>{m}</button>
        ))}
        <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>
          Week ending <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{weekEnding}</span>
        </div>
      </div>

      {/* Filters Row 2 — Brand + Assortment + Sub-category */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--text-muted)', fontSize: 12, marginRight: 4 }}>Brand</span>
        {['All', 'Bhuja', 'Infuzions', 'Infuzions Multipack'].map(b => (
          <button key={b} className={brand === b ? 'pill-active' : 'pill-inactive'} onClick={() => handleBrandChange(b)}>
            {b === 'Infuzions Multipack' ? 'Infuzions MP' : b}
          </button>
        ))}
        <div style={{ width: 1, height: 20, background: 'var(--border-soft)', margin: '0 8px' }} />
        <span style={{ color: 'var(--text-muted)', fontSize: 12, marginRight: 4 }}>Assortment</span>
        {assortmentOptions.map(a => (
          <button key={a} className={assortment === a ? 'pill-active' : 'pill-inactive'} onClick={() => setAssortment(a)}
            style={{ fontSize: 11 }}>
            {a === 'All' ? 'All' : a}
          </button>
        ))}
        <div style={{ width: 1, height: 20, background: 'var(--border-soft)', margin: '0 8px' }} />
        <span style={{ color: 'var(--text-muted)', fontSize: 12, marginRight: 4 }}>Sub-cat</span>
        {SUBCATEGORIES.map(s => (
          <button key={s} className={subcat === s ? 'pill-active' : 'pill-inactive'} onClick={() => setSubcat(s)}
            style={{ fontSize: 11 }}>
            {s}
          </button>
        ))}
      </div>

      {/* KPI Row */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {showWW && <>
          <KPICard label={`WW YTD ${metric}`} value={fmtMetric(wwYTD)} delta={calcDelta(wwYTD, wwYTDLY)} deltaLabel="vs LY" accent="#1a8a3f" />
          <KPICard label={`WW W/E ${weekEnding}`} value={fmtMetric(wwLatestDisp)} delta={calcDelta(wwLatestDisp, wwLatestLYDisp)} deltaLabel="vs LY" />
        </>}
        {showColes && <>
          <KPICard label={`Coles YTD ${metric}`} value={fmtMetric(colesYTD)} delta={calcDelta(colesYTD, colesYTDLY)} deltaLabel="vs LY" accent="#e03131" />
          <KPICard label={`Coles W/E ${weekEnding}`} value={fmtMetric(colesLatestDisp)} delta={calcDelta(colesLatestDisp, colesLatestLYDisp)} deltaLabel="vs LY" />
        </>}
      </div>

      {/* Weekly Sales Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: showWW && showColes ? '1fr 1fr' : '1fr', gap: 16 }}>
        {showWW && (
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: '#1a8a3f' }} />
                <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Woolworths Weekly Sales</span>
              </div>
              {(brand !== 'All' || assortment !== 'All' || subcat !== 'All') && (
                <span style={{ fontSize: 11, color: isEstimate ? 'var(--warn)' : 'var(--text-muted)' }}>
                  {assortment !== 'All' ? assortment : brand !== 'All' ? brand : subcat}
                  {isEstimate ? ' (est.)' : ''}
                </span>
              )}
            </div>
            <WeeklySalesChart data={filteredWeekly} retailer="Woolworths" weeks={weeks} metric={metric} />
          </div>
        )}
        {showColes && (
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: '#e03131' }} />
                <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>Coles Weekly Sales</span>
              </div>
              {(brand !== 'All' || assortment !== 'All' || subcat !== 'All') && (
                <span style={{ fontSize: 11, color: isEstimate ? 'var(--warn)' : 'var(--text-muted)' }}>
                  {assortment !== 'All' ? assortment : brand !== 'All' ? brand : subcat}
                  {isEstimate ? ' (est.)' : ''}
                </span>
              )}
            </div>
            <WeeklySalesChart data={filteredWeekly} retailer="Coles" weeks={weeks} metric={metric} />
          </div>
        )}
      </div>

      {/* Market Share Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: showWW && showColes ? '1fr 1fr' : '1fr', gap: 16 }}>
        {showWW && (
          <div className="card">
            <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12 }}>Woolworths — Market Share %</div>
            <MarketShareChart data={weekly} retailer="Woolworths" weeks={weeks} />
          </div>
        )}
        {showColes && (
          <div className="card">
            <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12 }}>Coles — Market Share %</div>
            <MarketShareChart data={weekly} retailer="Coles" weeks={weeks} />
          </div>
        )}
      </div>

      {/* Brand YTD Table */}
      <div className="card">
        <div style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: 16 }}>Brand YTD Performance (SCAN) — YTD to {weekEnding}</div>
        <table>
          <thead>
            <tr>
              <th>Brand / Family</th>
              <th style={{ textAlign: 'right' }}>WW YTD TY</th>
              <th style={{ textAlign: 'right' }}>WW YTD LY</th>
              <th style={{ textAlign: 'right' }}>WW Δ%</th>
              <th style={{ textAlign: 'right' }}>Coles YTD TY</th>
              <th style={{ textAlign: 'right' }}>Coles YTD LY</th>
              <th style={{ textAlign: 'right' }}>Coles Δ%</th>
              <th style={{ textAlign: 'right' }}>Total TY</th>
              <th style={{ textAlign: 'right' }}>Total Δ%</th>
            </tr>
          </thead>
          <tbody>
            {brandData
              .filter(b => brand === 'All' || b.family === brand)
              .map(b => {
                const wwD = calcDelta(b.ww_ytd_ty, b.ww_ytd_ly);
                const colesD = calcDelta(b.coles_ytd_ty, b.coles_ytd_ly);
                const totalTY = (b.ww_ytd_ty || 0) + (b.coles_ytd_ty || 0);
                const totalLY = (b.ww_ytd_ly || 0) + (b.coles_ytd_ly || 0);
                const totalD = calcDelta(totalTY, totalLY);
                return (
                  <tr key={b.family}>
                    <td style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{b.family}</td>
                    <td style={{ textAlign: 'right', color: 'var(--text-primary)' }}>{fmt(b.ww_ytd_ty)}</td>
                    <td style={{ textAlign: 'right', color: 'var(--text-body)' }}>{fmt(b.ww_ytd_ly)}</td>
                    <td style={{ textAlign: 'right' }}><span className={wwD >= 0 ? 'positive' : 'negative'}>{fmtPct(wwD)}</span></td>
                    <td style={{ textAlign: 'right', color: 'var(--text-primary)' }}>{fmt(b.coles_ytd_ty)}</td>
                    <td style={{ textAlign: 'right', color: 'var(--text-body)' }}>{fmt(b.coles_ytd_ly)}</td>
                    <td style={{ textAlign: 'right' }}><span className={colesD >= 0 ? 'positive' : 'negative'}>{fmtPct(colesD)}</span></td>
                    <td style={{ textAlign: 'right', color: 'var(--info)', fontWeight: 600 }}>{fmt(totalTY)}</td>
                    <td style={{ textAlign: 'right' }}><span className={totalD >= 0 ? 'positive' : 'negative'}>{fmtPct(totalD)}</span></td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
